import React from "react";
import { ArrowRight, Target, Eye, Users } from "lucide-react";
import PageHero from "@/components/PageHero";
import FloatingEditButton from "@/components/FloatingEditButton";
import MiembroCard from "@/components/MiembroCard";
import CarouselSection from "@/components/CarouselSection";
import ServiceFlipCard from "@/components/ServiceFlipCard";
import SEO from "@/components/SEO";
import Reveal from "@/components/motion/Reveal";
import Section, { SectionHeading } from "@/components/ui/Section";
import CTAButton from "@/components/ui/CTAButton";
import EmptyState from "@/components/ui/EmptyState";
import { CardGridSkeleton } from "@/components/ui/Skeleton";
import { useTeam } from "@/hooks/useTeam";
import { apiGet } from "@/lib/api";
import useCachedResource from "@/hooks/useCachedResource";

// Valores institucionales — respaldo si el CMS no devuelve la sección.
// El contenido editable vive en el CMS (sección "valores-quienes-somos").
const DEFAULT_VALORES = [
  { icon: "Heart", title: "Comunión", desc: "Anunciamos el Evangelio desde la unidad de la Iglesia, conectados con nuestra diócesis y en comunión con el Papa y los obispos." },
  { icon: "Target", title: "Misión", desc: "Llevamos el anuncio de Jesucristo a las periferias digitales, donde viven y se relacionan las personas de hoy." },
  { icon: "Users", title: "Servicio", desc: "Ponemos nuestros talentos digitales al servicio del Reino de Dios y de la evangelización de nuestra diócesis." },
  { icon: "Shield", title: "Ética Digital", desc: "Usamos la tecnología con responsabilidad moral, respetando la dignidad de la persona y los valores del Evangelio." },
  { icon: "Lightbulb", title: "Innovación", desc: "Buscamos nuevas formas creativas de comunicar la fe, sin perder la profundidad del mensaje cristiano." },
  { icon: "Eye", title: "Autenticidad", desc: "Somos testigos auténticos en el mundo digital, con coherencia entre lo que vivimos y lo que comunicamos." },
];

// Áreas de misión — respaldo si el CMS no devuelve la sección.
// Contenido editable en el CMS (sección "areas-quienes-somos").
const DEFAULT_AREAS = [
  { icon: "Wifi", title: "Evangelización en Redes Sociales", desc: "Gestionamos y creamos contenido evangelizador en Instagram, TikTok, Facebook y YouTube para llegar a miles de personas con el mensaje de Cristo.", features: ["Estrategia de contenido", "Calendario editorial", "Diseño gráfico", "Análisis de resultados"], href: "/eventos", cta: "Ver formación en redes" },
  { icon: "Video", title: "Creación de Contenido Audiovisual", desc: "Producimos vídeos, reels, podcasts y material multimedia que convierten el mensaje cristiano en contenido atractivo y compartible.", features: ["Producción de vídeo", "Edición profesional", "Podcast evangelizador", "Fotografía pastoral"], href: "/recursos", cta: "Ver recursos" },
  { icon: "GraduationCap", title: "Formación Digital para Agentes Pastorales", desc: "Ofrecemos cursos y talleres prácticos para sacerdotes, catequistas, jóvenes y voluntarios que quieren evangelizar en el mundo digital.", features: ["Cursos básico, medio y avanzado", "Formación para sacerdotes", "Talleres prácticos", "Formación en IA"], href: "/formacion", cta: "Ver formación" },
  { icon: "Church", title: "Acompañamiento a Parroquias y Grupos", desc: "Asesoramos y acompañamos a parroquias, movimientos y grupos eclesiales en su proceso de transformación y presencia digital.", features: ["Diagnóstico digital", "Plan de presencia online", "Formación in-situ", "Seguimiento continuo"], href: "/contacto", cta: "Contactar" },
  { icon: "Bot", title: "Innovación con Inteligencia Artificial", desc: "Exploramos y aplicamos las herramientas de inteligencia artificial para amplificar la evangelización con creatividad y responsabilidad ética.", features: ["Generación de contenido", "Automatización responsable", "Ética digital cristiana", "Formación en IA"], href: "/formacion", cta: "Ver formación en IA" },
  { icon: "Rocket", title: "Desarrollo de Proyectos Digitales", desc: "Diseñamos y desarrollamos plataformas, aplicaciones y proyectos digitales que potencian la misión evangelizadora de la Diócesis.", features: ["Diseño web pastoral", "Apps evangelizadoras", "Plataformas formativas", "Innovación diocesana"], href: "/contacto", cta: "Proponer proyecto" },
];

const QuienesSomos = () => {
  // `loading` sí se usa ahora: antes se descartaba, así que mientras llegaba
  // la respuesta se pintaba «El equipo se está configurando» y luego saltaba
  // al carrusel. Dos maquetaciones distintas en cada visita.
  const { miembros, loading: cargandoEquipo } = useTeam({ soloVisibles: true });

  // Secciones editables desde el CMS (/admin/paginas/quienes-somos)
  const { data } = useCachedResource("page:quienes-somos", () => apiGet("/pages/quienes-somos"));
  const sections = Array.isArray(data?.sections) ? data.sections : [];

  const findSection = id => sections.find(s => s.id === id && s.visible !== false);
  const valoresSec = findSection("valores-quienes-somos");
  const areasSec = findSection("areas-quienes-somos");
  const valoresCards = valoresSec?.content?.cards?.length ? valoresSec.content.cards : DEFAULT_VALORES;
  const areasCards = areasSec?.content?.cards?.length ? areasSec.content.cards : DEFAULT_AREAS;
  const valoresContent = valoresSec?.content || {};
  const areasContent = areasSec?.content || {};

  return (
    <div className="min-h-screen">
      <SEO
        title="Quiénes somos"
        description="La Delegación de Evangelización Digital de la Diócesis de Huelva: misión, visión, valores, áreas de trabajo y equipo."
      />
      <PageHero slug="quienes-somos" />
      <FloatingEditButton slug="quienes-somos" />

      {/* Presentación */}
      <Section tone="light" width="reading" innerClassName="text-center">
        <Reveal>
          <h2 className="font-heading font-bold mb-6 text-balance" style={{ fontSize: "var(--text-h2)" }}>
            La Delegación de Evangelización Digital
          </h2>
        </Reveal>
        <Reveal delay={80}>
          <p className="text-lg text-muted-foreground leading-relaxed mb-4">
            Somos un equipo de la <strong className="text-foreground font-semibold">Diócesis de Huelva</strong> comprometido
            con llevar el anuncio del Evangelio al mundo digital. Creemos que las redes sociales, los
            podcasts, los vídeos y las nuevas tecnologías son una oportunidad providencial para que la
            Iglesia llegue a las personas allí donde están.
          </p>
          <p className="text-lg text-muted-foreground leading-relaxed">
            Nuestra misión es evangelizar con creatividad, profundidad y fidelidad al pensamiento
            cristiano, acompañando a las personas en su camino de fe a través del entorno digital.
          </p>
        </Reveal>
      </Section>

      {/* Misión y visión */}
      <Section tone="muted" width="default" innerClassName="max-w-5xl">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
          {[
            {
              icon: Target,
              title: "Nuestra misión",
              text: "Evangelizar en el entorno digital de la Diócesis de Huelva con profundidad teológica, creatividad pastoral y fidelidad al Magisterio de la Iglesia, formando a misioneros digitales y produciendo contenido que ilumine la vida de las personas con la luz del Evangelio.",
            },
            {
              icon: Eye,
              title: "Nuestra visión",
              text: "Una Iglesia diocesana presente, activa y creíble en el mundo digital, capaz de dialogar con la cultura contemporánea y de ofrecer el mensaje de Jesucristo como respuesta a las preguntas más profundas del ser humano.",
            },
          ].map((bloque, i) => (
            <Reveal key={bloque.title} delay={i * 90} distance={28}>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center flex-shrink-0">
                  <bloque.icon size={20} className="text-primary-foreground" aria-hidden="true" />
                </div>
                <h3 className="text-xl font-heading font-bold">{bloque.title}</h3>
              </div>
              <p className="text-muted-foreground leading-relaxed">{bloque.text}</p>
            </Reveal>
          ))}
        </div>
      </Section>

      {/* Valores — tarjetas en carrusel, editables desde el CMS */}
      <CarouselSection
        label={valoresContent.label || "Nuestra identidad"}
        title={valoresContent.title || "Nuestros valores"}
        description={valoresContent.description || ""}
        tone="light"
      >
        {valoresCards.map((card, i) => (
          <ServiceFlipCard key={i} card={card} index={`valor-${i}`} />
        ))}
      </CarouselSection>

      {/* Áreas de misión — tarjetas en carrusel, editables desde el CMS */}
      <CarouselSection
        label={areasContent.label || "Qué hacemos"}
        title={areasContent.title || "Nuestras áreas de misión"}
        description={
          areasContent.description ||
          "Trabajamos en estos ámbitos para que el Evangelio llegue a toda persona en el entorno digital."
        }
        tone="light"
      >
        {areasCards.map((card, i) => (
          <ServiceFlipCard key={i} card={card} index={`area-${i}`} />
        ))}
      </CarouselSection>

      {/* Equipo */}
      {cargandoEquipo && miembros.length === 0 ? (
        <Section tone="muted">
          <SectionHeading label="Equipo" title="Nuestro equipo" />
          <CardGridSkeleton count={3} />
        </Section>
      ) : miembros.length === 0 ? (
        <Section tone="muted">
          <SectionHeading
            label="Equipo"
            title="Nuestro equipo"
            description="Personas comprometidas con la evangelización digital al servicio de la Diócesis de Huelva."
            align="center"
          />
          <EmptyState
            icon={Users}
            title="El equipo se está configurando"
            description="Próximamente conocerás a sus integrantes."
          />
        </Section>
      ) : (
        <CarouselSection
          label="Equipo"
          title="Nuestro equipo"
          description="Personas comprometidas con la evangelización digital al servicio de la Diócesis de Huelva."
          tone="muted"
        >
          {miembros.map(miembro => (
            <MiembroCard key={miembro.id} miembro={miembro} />
          ))}
        </CarouselSection>
      )}

      {/* Llamada a ser misionero digital */}
      <Section tone="dark" width="narrow" space="loose" innerClassName="text-center">
        <Reveal>
          <h2 className="font-heading font-bold mb-4 text-balance" style={{ fontSize: "var(--text-h2)" }}>
            ¿Quieres ser Misionero Digital?
          </h2>
          <p className="text-white/70 text-lg mb-10 leading-relaxed">
            Únete a nuestra red de misioneros digitales y ayúdanos a llevar el Evangelio a las redes
            sociales. No necesitas ser experto en tecnología, solo ganas de compartir tu fe.
          </p>
          <div className="flex justify-center">
            <CTAButton to="/misionero-digital" variant="light" size="lg" className="font-semibold">
              Quiero ser misionero digital <ArrowRight size={18} aria-hidden="true" />
            </CTAButton>
          </div>
        </Reveal>
      </Section>
    </div>
  );
};

export default QuienesSomos;
